import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    MenuItem,
    Button,
    Grid,
    Box,
    Stack,
    Typography,
    Avatar,
    IconButton,
    FormHelperText,
} from '@mui/material';
import {
    FaUser,
    FaCheckCircle,
    FaTimesCircle,
    FaCloudUploadAlt,
    FaTimes,
} from 'react-icons/fa';
import { getData } from '../../Webservice/Webservice';

const AddUserModal = ({
    open,
    handleClose,
    newUser,
    handleChange,
    handleSubmit,
    isEdit = false,
    editingUser = null,
    onPermissionsLoaded,
    asPage = false,
}) => {
    const [roles, setRoles] = useState([]);
    const [warehouses, setWarehouses] = useState([]);
    const [imagePreview, setImagePreview] = useState(null);
    const [permissions, setPermissions] = useState([]); // structured permissions for building options
    const [warehouseError, setWarehouseError] = useState('');

    // Validation state
    const [errors, setErrors] = useState({});
    const [formError, setFormError] = useState('');

    // Helper function to flatten permissions for Autocomplete
    const getAllPermissions = (permissionsList) => {
        const allPermissions = [];
        permissionsList.forEach((perm) => {
            allPermissions.push(perm.name);
            if (perm.children) {
                perm.children.forEach((child) => {
                    allPermissions.push(`${perm.name} > ${child}`);
                });
            }
        });
        return allPermissions;
    };

    const allPermissions = getAllPermissions(permissions);

    const handleImageUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (ev) => {
                const imageUrl = ev.target.result;
                setImagePreview(imageUrl);
                // keep original behavior: notify parent with preview/base64 (if your API expects File/FormData, adapt parent)
                handleChange({
                    target: {
                        name: 'image',
                        value: imageUrl
                    }
                });
                // clear any image related error
                setErrors(prev => ({ ...prev, image: undefined }));
            };
            reader.readAsDataURL(file);
        }
    };

    const removeImage = () => {
        setImagePreview(null);
        handleChange({
            target: {
                name: 'image',
                value: ''
            }
        });
    };

    useEffect(() => {
        if (!open) {
            // reset preview & errors on close
            setImagePreview(null);
            setWarehouseError('');
            setErrors({});
            setFormError('');
            return;
        }

        const tokenValue = sessionStorage.getItem('token');
        const token = tokenValue ? `Bearer ${tokenValue}` : '';

        // Async loader that fetches roles, menus, warehouses and optionally user detail
        const loadInitialData = async () => {
            try {
                // Fetch roles, menus and warehouses in parallel
                const [rolesRes, menusRes, warehousesRes] = await Promise.all([
                    getData('role/list?limit=100', null, token).catch(() => null),
                    getData('menu/list', undefined, token).catch(() => null),
                    getData('warehouse/list', { page: 1, limit: 100 }, token).catch(() => null)
                ]);

                if (rolesRes) {
                    const rList = Array.isArray(rolesRes) ? rolesRes : (rolesRes.roles || []);
                    setRoles(rList);
                }

                if (menusRes && Array.isArray(menusRes)) {
                    if (typeof onPermissionsLoaded === 'function') {
                        onPermissionsLoaded(menusRes);
                    }
                    const perms = menusRes.map((menu) => ({
                        name: menu.name,
                        children: menu.submenus && menu.submenus.length > 0 ? menu.submenus.map((sub) => sub.name) : undefined,
                        _raw: menu, // keep raw to help mapping from IDs/names if needed
                    }));
                    setPermissions(perms);
                }

                if (warehousesRes) {
                    const wList = Array.isArray(warehousesRes) ? warehousesRes : (warehousesRes.warehouse || []);
                    setWarehouses(wList);
                }

                // If editing, fetch user detail from separate API route and populate form fields
                if (isEdit) {
                    // support editingUser being id string or object
                    const userId = typeof editingUser === 'string' ? editingUser : (editingUser && editingUser._id ? editingUser._id : null);
                    if (userId) {
                        const detailRes = await getData(`user/${userId}`, undefined, token);
                        if (detailRes && detailRes.user) {
                            const u = detailRes.user;

                            // Map fields to parent form state using handleChange
                            handleChange({ target: { name: 'firstName', value: u.firstName || '' } });
                            handleChange({ target: { name: 'lastName', value: u.lastName || '' } });
                            handleChange({ target: { name: 'email', value: u.email || '' } });
                            handleChange({ target: { name: 'phoneNumber', value: u.phoneNumber || '' } });

                            // role_id in response is an object; set to _id if present
                            const roleId = u.role_id && (typeof u.role_id === 'object') ? u.role_id._id : (u.role_id || '');
                            handleChange({ target: { name: 'role_id', value: roleId } });

                            // status
                            handleChange({ target: { name: 'status', value: u.status || 'active' } });

                            // warehouse id - check both warehouseId and warehouse_id, handle objects or strings
                            const rawWarehouse = u.warehouseId || u.warehouse_id || '';
                            const warehouseIdValue = (rawWarehouse && typeof rawWarehouse === 'object') ? rawWarehouse._id : rawWarehouse;
                            handleChange({ target: { name: 'warehouseId', value: warehouseIdValue || '' } });

                            // image - set preview and notify parent value
                            if (u.image) {
                                setImagePreview(u.image);
                                handleChange({ target: { name: 'image', value: u.image } });
                            } else {
                                setImagePreview(null);
                                handleChange({ target: { name: 'image', value: '' } });
                            }

                            // Map accessibleMenus -> flattened permission strings expected by Autocomplete
                            const mappedPermissions = [];
                            if (Array.isArray(detailRes.accessibleMenus)) {
                                detailRes.accessibleMenus.forEach((menuItem) => {
                                    const menuName = menuItem.menuName;

                                    // if any of menuItem.permissions flags are true, include parent menu
                                    const permsObj = menuItem.permissions || {};
                                    const menuHasPermission = Boolean(permsObj.read || permsObj.create || permsObj.update || permsObj.delete);
                                    if (menuHasPermission) {
                                        mappedPermissions.push(menuName);
                                    }

                                    // submenus: include each submenu if its permissions object has any true
                                    if (Array.isArray(menuItem.submenus)) {
                                        menuItem.submenus.forEach((sub) => {
                                            const subPerms = sub.permissions || {};
                                            const subHasPermission = Boolean(subPerms.read || subPerms.create || subPerms.update || subPerms.delete);
                                            if (subHasPermission) {
                                                // add "Parent > Child" format
                                                mappedPermissions.push(`${menuName} > ${sub.submenuName}`);
                                                // ensure parent is included (already pushed above if menuHasPermission false)
                                                if (!menuHasPermission) mappedPermissions.push(menuName);
                                            }
                                        });
                                    }
                                });
                            }

                            // dedupe and set only those options that exist in our flattened options (allPermissions)
                            const unique = Array.from(new Set(mappedPermissions));
                            const filtered = unique.filter((v) => {
                                // if menusRes is not fetched yet allPermissions could be empty; still set mappedPermissions raw in that case
                                if (allPermissions && allPermissions.length > 0) {
                                    return allPermissions.includes(v);
                                }
                                return true;
                            });

                            // set permissions into parent state
                            handleChange({ target: { name: 'permissions', value: filtered } });
                        }
                    }
                } else {
                    // not edit: clear image in parent
                    handleChange({ target: { name: 'image', value: '' } });
                    handleChange({ target: { name: 'permissions', value: [] } });
                }
            } catch (err) {
                // silent: you can add logging here if you want
                // console.error(err);
            }
        };

        loadInitialData();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, isEdit, editingUser, onPermissionsLoaded]);

    // Check if the selected role is warehouse_admin
    const isWarehouseAdmin = roles.find(role => role.name && role.name.trim().toLowerCase() === 'warehouse_admin')?._id === newUser.role_id;

    // Validate warehouseId when role is warehouse_admin
    useEffect(() => {
        if (isWarehouseAdmin && !newUser.warehouseId) {
            setWarehouseError('Please select a warehouse');
        } else {
            setWarehouseError('');
        }

        // Clear warehouseId ONLY when the user explicitly switches away from warehouse_admin role
        // Do NOT clear when editing an existing warehouse_admin (isEdit=true) during initial role load
        if (roles.length > 0 && !isWarehouseAdmin && newUser.warehouseId && !isEdit) {
            handleChange({
                target: {
                    name: 'warehouseId',
                    value: ''
                }
            });
        }
    }, [isWarehouseAdmin, newUser.warehouseId, handleChange, roles, isEdit]);

    // Validate form fields
    const validateForm = () => {
        const newErrors = {};

        // First and Last Name
        if (!newUser.firstName || !newUser.firstName.toString().trim()) {
            newErrors.firstName = 'First name is required';
        }
        if (!newUser.lastName || !newUser.lastName.toString().trim()) {
            newErrors.lastName = 'Last name is required';
        }

        // Email
        if (!newUser.email || !newUser.email.toString().trim()) {
            newErrors.email = 'Email is required';
        } else if (!/^\S+@\S+\.\S+$/.test(newUser.email)) {
            newErrors.email = 'Invalid email format';
        }

        // Phone number (normalize and validate 10 digits)
        const rawPhone = (newUser.phoneNumber || '').toString();
        const cleanedPhone = rawPhone.replace(/\D/g, '');
        if (!cleanedPhone) {
            newErrors.phoneNumber = 'Mobile number is required';
        } else if (!/^\d{10}$/.test(cleanedPhone)) {
            newErrors.phoneNumber = 'Enter a valid 10-digit mobile number';
        }

        // Role
        if (!newUser.role_id) {
            newErrors.role_id = 'Please select a role';
        }

        // Warehouse required for warehouse_admin role
        if (isWarehouseAdmin && !newUser.warehouseId) {
            newErrors.warehouseId = 'Please select a warehouse';
        }

        // Password: required on create, optional on edit but must be >= 6 chars if provided
        if (!isEdit) {
            if (!newUser.password) {
                newErrors.password = 'Password is required';
            } else if (newUser.password.length < 6) {
                newErrors.password = 'Password must be at least 6 characters';
            }
        } else if (newUser.password && newUser.password.length < 6) {
            newErrors.password = 'Password must be at least 6 characters';
        }

        setErrors(newErrors);
        // set a general form error for UI if needed
        if (Object.keys(newErrors).length > 0) {
            setFormError('Please fix the errors highlighted below.');
        } else {
            setFormError('');
        }

        return Object.keys(newErrors).length === 0;
    };

    // Clear specific errors when newUser changes (so UX updates live)
    useEffect(() => {
        // simple cleanup: if a field has an error and it becomes valid, remove the error
        setErrors(prev => {
            const copy = { ...prev };
            if (copy.firstName && newUser.firstName && newUser.firstName.toString().trim()) delete copy.firstName;
            if (copy.lastName && newUser.lastName && newUser.lastName.toString().trim()) delete copy.lastName;
            if (copy.email && newUser.email && /^\S+@\S+\.\S+$/.test(newUser.email)) delete copy.email;
            const cleanedPhone = (newUser.phoneNumber || '').toString().replace(/\D/g, '');
            if (copy.phoneNumber && cleanedPhone && /^\d{10}$/.test(cleanedPhone)) delete copy.phoneNumber;
            if (copy.role_id && newUser.role_id) delete copy.role_id;
            if (copy.password && (isEdit || (newUser.password && newUser.password.length >= 6))) delete copy.password;
            if (copy.warehouseId && (!isWarehouseAdmin || newUser.warehouseId)) delete copy.warehouseId;
            if (copy.image && (isEdit || newUser.image || imagePreview)) delete copy.image;
            return copy;
        });
        // clear formError if no errors remain
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [newUser, imagePreview, isWarehouseAdmin]);

    // Local submit handler - run validation then call parent's handleSubmit
    const onFormSubmit = (e) => {
        // defensive: allow call without event
        if (e && typeof e.preventDefault === 'function') e.preventDefault();

        const ok = validateForm();
        if (!ok) return;

        // Pass the event to parent's handleSubmit so it can call preventDefault or use the event.
        if (typeof handleSubmit === 'function') {
            handleSubmit(e);
        }
    };

    const content = (
        <>
            <DialogTitle sx={{
                fontWeight: 700,
                color: 'text.primary',
                fontSize: '1.5rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                py: 2,
                px: 3,
            }}>
                <Typography variant="h5" fontWeight={700} sx={{ fontFamily: 'Mulish, sans-serif' }}>
                    <span>{isEdit ? 'Edit User Details' : 'Create New User'}</span>
                </Typography>
                {asPage ? (
                    <Button
                        onClick={handleClose}
                        sx={{
                            background: 'red',
                            color: 'white',
                            fontFamily: 'Mulish, sans-serif',
                            fontWeight: 700,
                            '&:hover': {
                                background: 'red'
                            }
                        }}
                    >
                        Back
                    </Button>
                ) : (
                    <IconButton onClick={handleClose} size="small" sx={{ color: 'text.secondary' }}>
                        <FaTimes />
                    </IconButton>
                )}
            </DialogTitle>

            <form onSubmit={onFormSubmit}>
                <DialogContent sx={{ p: 0 }}>
                    <Grid container>
                        {/* Left Side - Image Upload */}
                        <Grid item xs={12} md={4} sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            p: 4,
                        }}>
                            <Box sx={{
                                position: 'relative',
                                mb: 2,
                                width: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center'
                            }}>
                                <Avatar
                                    src={imagePreview}
                                    sx={{
                                        width: 150,
                                        height: 150,
                                        fontSize: '3.5rem',
                                        mb: 2,
                                        boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)'
                                    }}
                                >
                                    {!imagePreview && <FaUser />}
                                </Avatar>
                                {imagePreview && (
                                    <IconButton
                                        onClick={removeImage}
                                        sx={{
                                            position: 'absolute',
                                            top: 10,
                                            right: 'calc(50% - 75px)',
                                            bgcolor: 'error.main',
                                            color: 'white',
                                            '&:hover': {
                                                bgcolor: 'error.dark'
                                            }
                                        }}
                                        size="small"
                                    >
                                        <FaTimes fontSize="0.8rem" />
                                    </IconButton>
                                )}
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontFamily: 'Mulish, sans-serif' }}>
                                    Profile Photo
                                </Typography>
                                <Button
                                    component="label"
                                    variant="outlined"
                                    startIcon={<FaCloudUploadAlt />}
                                    sx={{
                                        width: '100%',
                                        maxWidth: '200px',
                                        borderRadius: 2,
                                        textTransform: 'none',
                                        fontWeight: 500,
                                        fontFamily: 'Mulish, sans-serif',
                                        color: 'rgb(255 209 2)',
                                        borderColor: 'rgb(255 209 2)',
                                        '&:hover': {
                                            borderColor: 'rgb(255 209 2)',
                                            backgroundColor: 'rgba(255, 209, 2, 0.1)',
                                        }
                                    }}
                                    size="medium"
                                >
                                    Choose Image
                                    <input
                                        type="file"
                                        accept="image/*"
                                        hidden
                                        onChange={handleImageUpload}
                                    />
                                </Button>
                                {errors.image && (
                                    <FormHelperText error sx={{ mt: 1 }}>
                                        {errors.image}
                                    </FormHelperText>
                                )}
                            </Box>
                        </Grid>

                        {/* Right Side - Form Fields */}
                        <Grid item xs={12} md={8} sx={{ p: 4 }}>
                            <Grid container spacing={3}>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        label="First Name"
                                        name="firstName"
                                        value={newUser.firstName || ''}
                                        onChange={handleChange}
                                        required
                                        fullWidth
                                        variant="outlined"
                                        size="small"
                                        sx={{ fontFamily: 'Mulish, sans-serif' }}
                                        error={!!errors.firstName}
                                        helperText={errors.firstName}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        label="Last Name"
                                        name="lastName"
                                        value={newUser.lastName || ''}
                                        onChange={handleChange}
                                        required
                                        fullWidth
                                        variant="outlined"
                                        size="small"
                                        sx={{ fontFamily: 'Mulish, sans-serif' }}
                                        error={!!errors.lastName}
                                        helperText={errors.lastName}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        label="Email"
                                        name="email"
                                        value={newUser.email || ''}
                                        onChange={handleChange}
                                        required
                                        type="email"
                                        fullWidth
                                        variant="outlined"
                                        size="small"
                                        sx={{ fontFamily: 'Mulish, sans-serif' }}
                                        error={!!errors.email}
                                        helperText={errors.email}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        label="Mobile Number"
                                        name="phoneNumber"
                                        value={newUser.phoneNumber || ''}
                                        onChange={handleChange}
                                        required
                                        fullWidth
                                        variant="outlined"
                                        size="small"
                                        sx={{ fontFamily: 'Mulish, sans-serif' }}
                                        error={!!errors.phoneNumber}
                                        helperText={errors.phoneNumber || '10 digits only'}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        select
                                        label="Role"
                                        name="role_id"
                                        value={newUser.role_id || ''}
                                        onChange={(e) => {
                                            handleChange(e);
                                            // Handle auto-assign permissions based on selected role
                                            const roleId = e.target.value;
                                            // Use a setTimeout to allow handleChange to process role_id first, then evaluate menus
                                            setTimeout(() => {
                                                const selectedRole = roles.find(r => r._id === roleId);
                                                if (selectedRole && selectedRole.menus && selectedRole.name !== 'super_admin' && selectedRole.name !== 'warehouse_admin') {
                                                    const roleMenuIds = selectedRole.menus.map(m => typeof m === 'object' ? m._id.toString() : m.toString());
                                                    const mappedPermissions = [];

                                                    permissions.forEach(perm => {
                                                        const rawMenu = perm._raw;
                                                        if (roleMenuIds.includes(rawMenu._id.toString())) {
                                                            mappedPermissions.push(perm.name);
                                                        }
                                                        if (rawMenu.submenus) {
                                                            rawMenu.submenus.forEach(sub => {
                                                                if (roleMenuIds.includes(sub._id.toString())) {
                                                                    mappedPermissions.push(`${perm.name} > ${sub.name}`);
                                                                    if (!mappedPermissions.includes(perm.name)) {
                                                                        mappedPermissions.push(perm.name);
                                                                    }
                                                                }
                                                            });
                                                        }
                                                    });

                                                    const uniquePerms = [...new Set(mappedPermissions)];
                                                    handleChange({ target: { name: 'permissions', value: uniquePerms } });
                                                } else if (selectedRole && (selectedRole.name === 'super_admin' || selectedRole.name === 'warehouse_admin')) {
                                                    // For super admin and warehouse admin, backend handles giving full permissions dynamically.
                                                    handleChange({ target: { name: 'permissions', value: [] } });
                                                }
                                            }, 0);
                                        }}
                                        required
                                        fullWidth
                                        variant="outlined"
                                        size="small"
                                        sx={{ fontFamily: 'Mulish, sans-serif' }}
                                        error={!!errors.role_id}
                                        helperText={errors.role_id}
                                    >
                                        {roles.length > 0 ? (
                                            roles
                                                .filter(r => {
                                                    // Show all admin roles + warehouse_admin only
                                                    if (r.roleFor === 'admin') return true;
                                                    if (r.name && r.name.trim().toLowerCase() === 'warehouse_admin') return true;
                                                    return false;
                                                })
                                                .map((role) => (
                                                    <MenuItem key={role._id} value={role._id}>
                                                        {role.name.charAt(0).toUpperCase() + role.name.slice(1).replace(/_/g, ' ')}
                                                    </MenuItem>
                                                ))
                                        ) : (
                                            <MenuItem disabled>Loading roles...</MenuItem>
                                        )}
                                    </TextField>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        label="Password"
                                        name="password"
                                        type="password"
                                        value={newUser.password || ''}
                                        onChange={handleChange}
                                        required={!isEdit}
                                        fullWidth
                                        variant="outlined"
                                        size="small"
                                        sx={{ fontFamily: 'Mulish, sans-serif' }}
                                        error={!!errors.password}
                                        helperText={errors.password || (!isEdit ? 'Minimum 6 characters' : '')}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        select
                                        label="Status"
                                        name="status"
                                        value={newUser.status || 'active'}
                                        onChange={handleChange}
                                        required
                                        fullWidth
                                        variant="outlined"
                                        size="small"
                                        sx={{ fontFamily: 'Mulish, sans-serif' }}
                                        error={!!errors.status}
                                        helperText={errors.status}
                                    >
                                        <MenuItem value="active">
                                            <Stack direction="row" alignItems="center" spacing={1}>
                                                <FaCheckCircle style={{ color: '#4caf50' }} />
                                                <Typography>Active</Typography>
                                            </Stack>
                                        </MenuItem>
                                        <MenuItem value="inActive">
                                            <Stack direction="row" alignItems="center" spacing={1}>
                                                <FaTimesCircle style={{ color: '#f44336' }} />
                                                <Typography>Inactive</Typography>
                                            </Stack>
                                        </MenuItem>
                                    </TextField>
                                </Grid>
                                {/* Warehouse selector — only shown for warehouse_admin role */}
                                {isWarehouseAdmin && (
                                    <Grid item xs={12}>
                                        <TextField
                                            select
                                            label="Warehouse"
                                            name="warehouseId"
                                            value={newUser.warehouseId || ''}
                                            onChange={handleChange}
                                            required
                                            fullWidth
                                            variant="outlined"
                                            size="small"
                                            sx={{ fontFamily: 'Mulish, sans-serif' }}
                                            error={!!(warehouseError || errors.warehouseId)}
                                            helperText={warehouseError || errors.warehouseId || 'Select the warehouse this admin will manage'}
                                        >
                                            {warehouses.length > 0 ? (
                                                warehouses.map((warehouse) => (
                                                    <MenuItem key={warehouse._id} value={warehouse._id}>
                                                        {warehouse.name}
                                                    </MenuItem>
                                                ))
                                            ) : (
                                                <MenuItem disabled>No warehouses available</MenuItem>
                                            )}
                                        </TextField>
                                    </Grid>
                                )}
                            </Grid>
                        </Grid>
                    </Grid>
                </DialogContent>

                <DialogActions sx={{
                    p: 3,
                    pr: 4,
                    justifyContent: 'flex-end',
                }}>
                    <Box sx={{ flex: '1 1 auto' }}>
                        {formError && (
                            <Typography color="error" variant="body2" sx={{ mt: 0.5 }}>
                                {formError}
                            </Typography>
                        )}
                    </Box>
                    <Button
                        onClick={handleClose}
                        variant="outlined"
                        sx={{
                            minWidth: 120,
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 600,
                            mr: 2,
                            color: 'red',
                            fontFamily: 'Mulish, sans-serif',
                            borderColor: 'red',
                            '&:hover': {
                                borderColor: 'red',
                                backgroundColor: 'rgba(255, 0, 0, 0.1)',
                            },
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={isWarehouseAdmin && !newUser.warehouseId}
                        sx={{
                            minWidth: 120,
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 600,
                            color: 'black',
                            fontFamily: 'Mulish, sans-serif',
                            background: 'rgb(255 209 2)',
                            border: "1px solid black",
                            '&:hover': {
                                background: 'rgb(255 209 2)'
                            }
                        }}
                    >
                        {isEdit ? 'Update User' : 'Create User'}
                    </Button>
                </DialogActions>
            </form>
        </>
    );

    if (asPage) {
        return (
            <Box sx={{ boxShadow: '0 4px 20px rgba(0,0,0,0.08)', borderRadius: '12px' }}>
                {content}
            </Box>
        );
    }

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
                    minHeight: '500px'
                }
            }}
        >
            {content}
        </Dialog>
    );
};

export default AddUserModal;
